import React from 'react';
import { Box } from '@mui/material';
import { CircleExclamationIcon } from '@roku-web-ui/rds-icons';
import { colors } from '@roku-web-ui/rds-theme';

import { Disconnected } from 'components/camera/overlays/Disconnected';
import { BatteryEmpty } from 'components/camera/overlays/BatteryEmpty';
import { BatteryTimeout } from 'components/camera/overlays/BatteryTimeout';
import { NotSupported } from 'components/camera/overlays/NotSupported';
import { PoweredOff } from 'components/camera/overlays/PoweredOff';
import { Unavailable } from 'components/camera/overlays/Unavailable';
import { Loading } from 'components/common/Loading';

import { AppDeviceLeaf } from 'types/common/leaf';

import styles from './cameraVideoOverlay.module.scss';

export type CameraVideoOverlayType = 'preview' | 'thumbnail';

interface CameraVideoOverlayProps {
  cameraVideoOverlayType?: CameraVideoOverlayType;
  leaf: AppDeviceLeaf;
  connect: () => Promise<void>;
  rtcConnection?: RTCPeerConnection;
  rtcLoading: boolean;
  rtcError?: Error;
}

export const CameraVideoOverlay = ({
  cameraVideoOverlayType,
  leaf,
  connect,
  rtcConnection,
  rtcLoading,
  rtcError,
}: CameraVideoOverlayProps) => {
  if (rtcLoading) {
    return (
      <Box className={styles.overlay}>
        <Loading />
      </Box>
    );
  }

  if (cameraVideoOverlayType === 'preview') {
    return (
      <Box className={styles.overlay}>
        <CircleExclamationIcon sx={{ color: colors.grey[400], fontSize: 36 }} />
      </Box>
    );
  }

  if (leaf.state?.battery?.level === 0) {
    return (
      <Box className={styles.overlay}>
        <BatteryEmpty />
      </Box>
    );
  }

  // Camera is not available
  if (leaf.state?.online?.online === false) {
    return (
      <Box className={styles.overlay}>
        <Unavailable />
      </Box>
    );
  }

  if (leaf.state?.power?.power === 'off') {
    return (
      <Box className={styles.overlay}>
        <PoweredOff leaf={leaf} />
      </Box>
    );
  }

  if (rtcError?.name === 'OperationError' || rtcError?.name === 'InvalidAccessError') {
    return (
      <Box className={styles.overlay}>
        <NotSupported />
      </Box>
    );
  }

  if (rtcConnection?.batteryTimeout || rtcError?.message === 'BatteryTimeout') {
    return (
      <Box className={styles.overlay}>
        <BatteryTimeout connect={connect} />
      </Box>
    );
  }

  return (
    <Box className={styles.overlay}>
      <Disconnected connect={connect} />
    </Box>
  );
};
